// client/src/components/ArticleSidebar.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/ArticleSidebar.css';

// Composants SVG inline
const IconClock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const IconCrown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
);

const IconQuestions = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const ArticleSidebar = ({ articlesConnexes = [], papabili = [] }) => {
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [hasFetched, setHasFetched] = useState(false); // Nouvel état pour suivre si nous avons déjà essayé de récupérer les données
  
  // Image de fallback pour les articles sans image
  const defaultArticleImage = '/images/article-default.png';
  
  // Image de fallback pour les cardinaux sans photo
  const defaultCardinalImage = '/images/cardinal-default.png';
  
  useEffect(() => {
    // Si nous avons des articles connexes, nous n'avons pas besoin de charger les articles récents
    if (articlesConnexes.length > 0) {
      setLoading(false);
      return;
    }
    
    // Si nous avons déjà essayé de récupérer les données, ne le refaisons pas
    if (hasFetched) {
      return;
    }
    
    const fetchRecentArticles = async () => {
      try {
        setLoading(true);
        
        // Récupérer les articles récents pour la sidebar si non fournis
        const response = await axios.get('/api/articles/recent?limit=5');
        setRecentArticles(response.data || []);
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des articles récents:', err);
        setError('Impossible de charger les articles récents');
        setLoading(false);
        // Même en cas d'erreur, on marque comme ayant essayé
        setRecentArticles([]); // Initialiser avec un tableau vide en cas d'erreur
      } finally {
        setHasFetched(true); // Marquer que nous avons essayé de récupérer les données
      }
    };
    
    fetchRecentArticles();
  }, [articlesConnexes, hasFetched]); // Ajout de hasFetched comme dépendance
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, itemId) => {
    if (!originalUrl || imageErrors[itemId]) return defaultArticleImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Obtenir l'URL de l'image du cardinal
  const getCardinalImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultCardinalImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (itemId) => {
    console.log(`Erreur de chargement d'image pour l'item ${itemId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [itemId]: true
    }));
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Articles à afficher (connexes ou récents)
  const articlesToShow = articlesConnexes.length > 0 ? articlesConnexes : recentArticles;
  
  // Affichage pour les articles
  const renderArticles = () => {
    if (loading) {
      return <div className="loading-placeholder">Chargement...</div>;
    }
    
    if (error) {
      return <div className="no-content">{error}</div>;
    }
    
    if (articlesToShow.length === 0) {
      return <p className="no-content">Aucun article récent disponible</p>;
    }
    
    return (
      <div className="sidebar-article-list">
        {articlesToShow.map(article => (
          <Link 
            to={`/actualites/${article.slug}`} 
            key={article._id || article.id} 
            className="sidebar-article-item"
          >
            <div className="sidebar-article-image">
              <img 
                src={getApiImageUrl(article.image, article._id || article.id)} 
                alt={article.titre} 
                onError={() => handleImageError(article._id || article.id)}
              />
            </div>
            <div className="sidebar-article-info">
              <h4>{article.titre}</h4>
              <div className="sidebar-article-date">
                <IconClock /> {formatDate(article.datePublication)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  };
  
  // Affichage pour les papabili
  const renderPapabili = () => {
    if (papabili.length === 0) {
      return <p className="no-content">Aucun papabile disponible</p>;
    }
    
    return (
      <div className="sidebar-papabili-list">
        {papabili.map(cardinal => (
          <Link 
            to={`/cardinaux/${cardinal.slug}`} 
            key={cardinal._id} 
            className="sidebar-papabile-item"
          >
            <div className="sidebar-papabile-image">
              <img 
                src={getCardinalImageUrl(cardinal.photo, cardinal._id)} 
                alt={cardinal.nomComplet} 
                onError={() => handleImageError(cardinal._id)}
              />
            </div>
            <div className="sidebar-papabile-info">
              <h4>{cardinal.nomComplet}</h4>
              <p>{cardinal.titre || cardinal.ordre}</p>
              <span className="sidebar-papabile-country">{cardinal.paysService}</span>
            </div>
          </Link>
        ))}
      </div>
    );
  };
  
  return (
    <div className="article-sidebar">
      {/* Section des articles récents */}
      <div className="sidebar-section recent-articles">
        <h3>Articles récents</h3>
        {renderArticles()}
      </div>
      
      {/* Section des papabili mis en avant */}
      <div className="sidebar-section papabili-feature">
        <h3>
          <IconCrown /> Papabili à suivre
        </h3>
        {renderPapabili()}
        <div className="sidebar-section-link">
          <Link to="/papabili" className="view-all">
            Voir tous les papabili
          </Link>
        </div>
      </div>
      
      {/* Section des liens vers d'autres pages */}
      <div className="sidebar-section quick-links">
        <h3>Explorer</h3>
        
        <div className="sidebar-links">
          <Link to="/cardinaux" className="sidebar-link">
            <div className="sidebar-link-icon">
              <IconCrown />
            </div>
            <div className="sidebar-link-text">
              <h4>Annuaire des Cardinaux</h4>
              <p>Découvrez tous les cardinaux électeurs</p>
            </div>
          </Link>
          
          <Link to="/conclave" className="sidebar-link">
            <div className="sidebar-link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 16.25v-3.5m0-9.5v3.5m0 0c1 0 1.75.75 1.75 1.75S13 12.25 12 12.25s-1.75-.75-1.75-1.75S11 8.75 12 8.75z"/>
              </svg>
            </div>
            <div className="sidebar-link-text">
              <h4>Conclave Virtuel</h4>
              <p>Participez à la simulation du conclave</p>
            </div>
          </Link>
          
          <Link to="/quiz" className="sidebar-link">
            <div className="sidebar-link-icon">
              <IconQuestions />
            </div>
            <div className="sidebar-link-text">
              <h4>Quiz sur le Conclave</h4>
              <p>Testez vos connaissances sur le Vatican</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticleSidebar;